/**
 * StrategicPositioning — site-wide "defensible Japan ETC workflow platform"
 * messaging layer (2026-06).
 *
 * COMPLIANCE: All copy here is customer-facing and is scanned by the
 * 景品表示法 public-copy guard. It MUST stay compliant — no official /
 * authority / tax-guarantee / misuse-detection / official-replacement /
 * superiority claims, and no investor-style strategy jargon on customer
 * pages. Use careful framing: "aims to become", "built around", "designed
 * for", "AI-assisted review", "official-source document handling",
 * "independent support service".
 *
 * On-brand blue palette: navy #0A192F · brand blue #0066CC · green accent #62BB46.
 */
import {
  Workflow, FileCheck2, Languages, Bot, Building2, ShieldCheck,
  ListChecks, ScrollText, CircleHelp, Sparkles,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/* 1. About Us / Corporate Profile — origin + long-term focus          */
/* ------------------------------------------------------------------ */
export function WorkflowOriginSections({ ja }) {
  return (
    <>
      <section className="py-12 bg-white" data-testid="positioning-origin">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1 h-4 rounded-full bg-[#0066CC]" aria-hidden="true" />
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0066CC]">
              {ja ? '私たちの背景' : 'Our background'}
            </p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight">
            {ja ? '日本のETC運用経験から生まれたサービス' : 'Built from real Japan ETC operating experience'}
          </h2>
          <p className={`mt-4 text-sm sm:text-base text-slate-700 leading-relaxed ${ja ? 'jtr-ja-text' : ''}`}>
            {ja
              ? 'Japan Toll Receiptsは、日本でETCカード利用者をサポートしてきた実務経験から生まれました。JapanETCcard.comを通じて、個人・家族・法人・団体のお客様が、毎月のETC利用記録を取得し、整理し、確認する作業に大きな手間を感じていることを見てきました。JTRは、公式元資料の取得・整理、日英バイリンガルのレポート配信、AIによる確認支援を組み合わせることで、日々の事務作業を減らすために開発されています。'
              : 'Japan Toll Receipts was developed from practical experience supporting ETC card users in Japan. Through JapanETCcard.com and related customer workflows, we have seen how difficult it can be for individuals, families, businesses, and organizations to collect, organize, and review ETC usage records every month. JTR was created to reduce that daily administrative burden by combining official-source document retrieval, bilingual report delivery, and AI-assisted review tools.'}
          </p>
        </div>
      </section>

      <section className="py-12 bg-slate-50 border-y border-slate-100" data-testid="positioning-longterm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1 h-4 rounded-full bg-[#0066CC]" aria-hidden="true" />
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0066CC]">
              {ja ? '私たちの長期的な取り組み' : 'Our long-term focus'}
            </p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight">
            {ja ? '私たちの長期的な取り組み' : 'Our long-term focus'}
          </h2>
          <p className={`mt-4 text-sm sm:text-base text-slate-700 leading-relaxed ${ja ? 'jtr-ja-text' : ''}`}>
            {ja
              ? '私たちは、日本で有料道路利用記録をより分かりやすく管理したい個人・法人・団体に向けて、信頼される独立型ETC利用レポートサービスを目指しています。不要な複雑さを増やすのではなく、安定した配信、分かりやすい表現、日英対応、資料の追跡性、実務に役立つ自動化を重視しています。'
              : 'Our goal is to build a reliable, independent ETC usage reporting service for people and organizations that need clearer toll-road records in Japan. We focus on reliability, careful wording, bilingual support, document traceability, and practical workflow automation before adding unnecessary complexity.'}
          </p>
        </div>
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 2. How It Works — "more than a download tool" + AI-assisted note    */
/* ------------------------------------------------------------------ */
export function WorkflowDepthMore({ ja }) {
  return (
    <section className="py-14 sm:py-16 bg-white border-t border-slate-100" data-testid="positioning-more-than-download">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-7">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0066CC]">
            {ja ? 'ワークフロー全体で考える' : 'Workflow, end to end'}
          </span>
          <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0A192F]">
            {ja ? '単なるダウンロード支援にとどまらない理由' : 'Why this is more than a simple download tool'}
          </h2>
        </div>
        <p className={`text-sm sm:text-base text-slate-700 leading-relaxed ${ja ? 'jtr-ja-text' : ''}`}>
          {ja
            ? 'JTRは、お客様の同意に基づく接続、公式元資料の取得、PDF・CSVの整理、メール配信、Log IDによる記録管理、経費精算の確認支援、確認が必要な利用候補の提示まで、ETC利用レポートの一連の流れを前提に設計されています。AIは確認作業を支援しますが、JTRの価値はワークフロー全体にあります。'
            : 'JTR is designed around the full ETC reporting workflow: connecting with the customer’s consent, retrieving available official-source documents, organizing PDF/CSV files, delivering reports by email, keeping Log IDs, supporting reimbursement review, and helping surface usage records that may need attention. AI supports the review layer, but the value comes from the complete workflow.'}
        </p>
        <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50/50 p-4 flex items-start gap-3" data-testid="positioning-ai-assisted-note">
          <span className="w-8 h-8 rounded-lg bg-white border border-blue-200 flex items-center justify-center text-[#0066CC] flex-shrink-0">
            <Bot className="w-4 h-4" strokeWidth={2} />
          </span>
          <p className={`text-[13px] text-slate-700 leading-relaxed ${ja ? 'jtr-ja-text' : ''}`}>
            {ja
              ? 'AIは記録の整理や確認候補の提示を支援します。税務・法務・不正利用の最終判断を行うものではありません。'
              : 'AI helps organize and highlight records for review. It does not make final tax, legal, or misuse determinations.'}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 3. Trust — "Trust comes from workflow depth" + 5 pillars            */
/* ------------------------------------------------------------------ */
export function TrustWorkflowDepth({ ja }) {
  const pillars = ja
    ? [
        { Icon: FileCheck2, t: '公式元資料の取得・整理' },
        { Icon: Languages, t: '日英バイリンガル対応' },
        { Icon: ScrollText, t: 'Log IDによるレポート追跡' },
        { Icon: Bot, t: 'AIによる確認支援、最終判断ではありません' },
        { Icon: Workflow, t: '日本のETC運用経験に基づく設計' },
      ]
    : [
        { Icon: FileCheck2, t: 'Official-source document handling' },
        { Icon: Languages, t: 'Bilingual customer communication' },
        { Icon: ScrollText, t: 'Report traceability with Log IDs' },
        { Icon: Bot, t: 'AI-assisted review, not automatic judgment' },
        { Icon: Workflow, t: 'Built from Japan ETC operating experience' },
      ];
  return (
    <section className="py-12 bg-white border-t border-gray-100" data-testid="positioning-trust-depth">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0066CC] mb-1">
          {ja ? '信頼の根拠' : 'Why trust JTR'}
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight">
          {ja ? '信頼は、実務に基づくワークフローから' : 'Trust comes from workflow depth'}
        </h2>
        <p className={`text-sm text-slate-600 mt-3 leading-relaxed max-w-3xl ${ja ? 'jtr-ja-text' : ''}`}>
          {ja
            ? '信頼できるETCレポートサービスには、見やすい画面だけでは不十分です。公式元資料の慎重な取り扱い、お客様の同意、日英で分かりやすい説明、安全な用語選び、レポートの追跡性、実際のお客様の状況に対応できる設計が必要です。JTRは、これらの要件を前提に開発されています。'
            : 'A reliable ETC reporting service needs more than a good dashboard. It needs careful handling of official-source documents, clear customer consent, bilingual explanations, safe terminology, report traceability, and support for real customer situations. JTR is being built around those requirements from the start.'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
          {pillars.map((p, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-4 rounded-xl border bg-white border-slate-200 hover:border-blue-400 hover:shadow-sm transition-all"
              data-testid={`positioning-trust-pillar-${i}`}
            >
              <div className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center bg-blue-100 text-[#0066CC]">
                <p.Icon className="w-4 h-4" strokeWidth={2.2} />
              </div>
              <span className="text-[13px] font-semibold text-[#0A192F] leading-snug">{p.t}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 4. Features — "A workflow advantage, powered by AI" + bullets       */
/* ------------------------------------------------------------------ */
export function FeaturesWorkflowAdvantage({ ja }) {
  const bullets = ja
    ? [
        '日次・定期レポート配信',
        '公式元資料に基づくPDF・CSV整理',
        '確認が必要な利用傾向の候補提示',
        '登録車両の確認支援',
        '記録整理用資料の整理',
        '日英バイリンガルのレポート案内',
      ]
    : [
        'Daily and scheduled report delivery',
        'PDF/CSV organization from official-source records',
        'Possible usage-pattern review candidates',
        'Registered-vehicle review support',
        'Reimbursement support materials',
        'Bilingual report communication',
      ];
  return (
    <section className="bg-white border-t border-slate-200/70" data-testid="positioning-features-advantage">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-7 h-7 rounded-lg bg-blue-100 text-[#0066CC] flex items-center justify-center">
                <Sparkles className="w-4 h-4" strokeWidth={2.2} />
              </span>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0066CC]">
                {ja ? 'AI活用' : 'AI where it helps'}
              </p>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A192F] tracking-tight">
              {ja ? 'AIを活用したETCワークフロー支援' : 'A workflow advantage, powered by AI'}
            </h2>
            <p className={`mt-4 text-sm sm:text-base text-slate-700 leading-relaxed ${ja ? 'jtr-ja-text' : ''}`}>
              {ja
                ? 'JTRは、レポート情報の整理、要約の支援、確認が必要な利用傾向の提示、ETC利用記録の理解支援など、実務に役立つ部分でAIを活用します。サービスの基礎は、公式元資料とお客様が管理する設定にあります。'
                : 'JTR uses AI where it helps the workflow: organizing report information, supporting summaries, highlighting usage patterns that may need review, and helping customers understand their ETC records. The service remains grounded in official-source documents and customer-controlled settings.'}
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {bullets.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 p-3 rounded-xl border border-slate-200 bg-slate-50/60"
                data-testid={`positioning-features-bullet-${i}`}
              >
                <ListChecks className="w-4 h-4 text-[#0066CC] mt-0.5 flex-shrink-0" strokeWidth={2.2} />
                <span className="text-[12.5px] font-medium text-slate-700 leading-snug">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 5. Homepage — small "Built for Japan ETC workflows" support section */
/* ------------------------------------------------------------------ */
export function HomeWorkflowSection({ ja }) {
  return (
    <section className="py-12 sm:py-14 bg-white border-t border-slate-100" data-testid="positioning-home-workflow">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-blue-100 text-[#0066CC] mb-4">
          <Workflow className="w-5 h-5" strokeWidth={2} />
        </span>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight">
          {ja ? '日本のETCワークフローに合わせた設計' : 'Built for Japan ETC workflows'}
        </h2>
        <p className={`mt-4 text-sm sm:text-base text-slate-700 leading-relaxed max-w-2xl mx-auto ${ja ? 'jtr-ja-text' : ''}`}>
          {ja
            ? 'JTRは、日本のETC運用経験、公式元資料の取得・整理、日英バイリンガルのレポート配信、AIによる確認支援を組み合わせ、手作業を減らしながら有料道路利用記録を管理できるよう支援します。'
            : 'JTR combines Japan ETC operating experience, official-source document handling, bilingual report delivery, and AI-assisted review to help customers manage toll-road usage records with less manual work.'}
        </p>
      </div>
    </section>
  );
}
